import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ValidationErrorComponent } from '../../../../../components/core/validation-error/validation-error.component';
import { UserClearValidationError, UserCreateRequest, UserCreateSuccess, UserManageUpdateRequest, UserManageUpdateSuccess } from '../../../../../store/user/user.actions';
import { User } from '../../../../../store/user/user.models';
import { selectUserValidationErrors } from '../../../../../store/user/user.selectors';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';

type AddUserDialogData = {
  mode?: 'create' | 'edit';
  user?: User | null;
};

@Component({
  selector: 'app-add-user-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, ValidationErrorComponent, MatSelectModule, CommonModule],
  templateUrl: './add-user-dialog.component.html',
  styleUrl: './add-user-dialog.component.scss',
})
export class AddUserDialogComponent {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject(MatDialogRef<AddUserDialogComponent>);
  private readonly dialogData = inject<AddUserDialogData | null>(MAT_DIALOG_DATA, { optional: true });

  protected readonly validationErrors = signal<string[]>([]);
  protected readonly mode = signal<'create' | 'edit'>(this.dialogData?.mode === 'edit' ? 'edit' : 'create');
  protected readonly form = this.formBuilder.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
    countryCode: ['+1', [Validators.required]],
  });

  countries = signal([
    { code: '+91', flag: '🇮🇳' },
    { code: '+1', flag: '🇺🇸' },
    { code: '+44', flag: '🇬🇧' },
    { code: '+61', flag: '🇦🇺' },

  ]);


  constructor() {
    this.store.dispatch(UserClearValidationError());
    this.patchFormFromUser(this.dialogData?.user ?? null);

    this.store.select(selectUserValidationErrors)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((errors) => {
        this.validationErrors.set(Array.isArray(errors) ? errors as string[] : []);
      });

    this.actions$
      .pipe(
        ofType(UserCreateSuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ user }) => {
        this.dialogRef.close(user);
      });

    this.actions$
      .pipe(
        ofType(UserManageUpdateSuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ user }) => {
        this.dialogRef.close(user);
      });
  }

  protected dialogTitle(): string {
    return this.mode() === 'edit' ? 'Edit User' : 'Add User';
  }

  protected dialogDescription(): string {
    return this.mode() === 'edit'
      ? 'Update the selected organization user details.'
      : 'Create a user inside this organization with name, email, and phone number.';
  }

  protected submitLabel(): string {
    return this.mode() === 'edit' ? 'Save changes' : 'Add user';
  }

  protected close(): void {
    this.store.dispatch(UserClearValidationError());
    this.dialogRef.close();
  }

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.store.dispatch(UserClearValidationError());
    const payload = {
      name: this.form.controls.name.value?.trim() ?? '',
      email: this.form.controls.email.value?.trim() ?? '',
      phone: `${this.form.controls.countryCode.value ?? ''}${this.form.controls.phone.value?.trim() ?? ''}`,
    };

    if (this.mode() === 'edit' && this.dialogData?.user?.id) {
      this.store.dispatch(UserManageUpdateRequest({
        userId: this.dialogData.user.id,
        payload,
      }));
      return;
    }

    this.store.dispatch(UserCreateRequest({ payload }));
  }

  private patchFormFromUser(user: User | null): void {
    if (!user) {
      return;
    }

    const { countryCode, phone } = this.splitPhoneNumber(user.phone);
    this.form.patchValue({
      name: user.name ?? '',
      email: user.email ?? '',
      countryCode,
      phone,
    });
  }

  private splitPhoneNumber(value: string | null | undefined): { countryCode: string; phone: string } {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
      return { countryCode: '+1', phone: '' };
    }

    const match = this.countries().find((country) => normalized.startsWith(country.code));
    if (match) {
      return {
        countryCode: match.code,
        phone: normalized.slice(match.code.length),
      };
    }

    return {
      countryCode: '+1',
      phone: normalized.replace(/^\+/, ''),
    };
  }
}
